import api from '@/lib/api';

/**
 * Resolve a pool access code across every sport.
 *
 * Codes are minted from one shared namespace but each sport keeps its own
 * public lookup endpoint, so we probe them in order: golf (the original
 * flow, most codes) → football LMS → horse racing.
 *
 * Returns `null` only when every sport genuinely answered 404 ("no such
 * pool"). Any other failure (500, 429 from the rate limiter, network drop)
 * is RE-THROWN so callers can say "something went wrong" instead of
 * telling a player holding a valid code that it's invalid.
 *
 * `gatekeeperPath` is the pre-auth landing page; `warRoomPath` goes straight
 * to the sport's pick surface for an already-signed-in player.
 */
const SPORTS = [
  { sport: 'golf', path: (c) => `/public/room/${c}`, warRoom: (c) => `/joinpool/${c}/war-room` },
  { sport: 'football', path: (c) => `/public/lms/room/${c}`, warRoom: (c) => `/joinpool/${c}/lms` },
  { sport: 'horse-racing', path: (c) => `/public/racing/room/${c}`, warRoom: (c) => `/joinpool/${c}/racing` },
];

export async function resolvePoolCode(rawCode) {
  const code = (rawCode || '').trim().toUpperCase();
  if (!code) return null;

  let lastNonNotFound = null;

  for (const entry of SPORTS) {
    try {
      const { data } = await api.get(entry.path(code));
      const token = data.join_token || data.access_code || code;
      return {
        sport: entry.sport,
        code: token,
        gatekeeperPath: `/joinpool/${token}`,
        warRoomPath: entry.warRoom(token),
      };
    } catch (err) {
      // 404 = "not this sport", keep probing. Anything else is a real
      // failure we must not disguise as an invalid code.
      if (err.response?.status !== 404) lastNonNotFound = err;
    }
  }

  if (lastNonNotFound) throw lastNonNotFound;
  return null;
}
